import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'

interface FormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  icon?: ReactNode
  /** id of the <form> rendered as children; the footer's submit button targets it. */
  formId: string
  submitLabel: string
  pending: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** The form. Radix unmounts dialog content when closed, so the form starts fresh on every open. */
  children: ReactNode
}

/** Modal wrapper for a create/edit form with a Cancel / Save footer. */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  formId,
  submitLabel,
  pending,
  size,
  children,
}: FormDialogProps) {
  const { t } = useTranslation('academic')
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      icon={icon}
      size={size}
      dismissible={!pending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            {submitLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  )
}
